import React, { useEffect } from "react";
import { Grid2 as Grid, Grid2Props } from "@mui/material";
import { singleGridItem } from "../../fields/defaultStyles";
import FormLabel from "../base/FormLabel";
import SubForm from "../../fields/subForms/SubForm";
import FormSelectField from "../../fields/basic/SelectField";
import { useFormContext } from "react-hook-form";
import { getEnumName } from "../../../field/FieldComponentUtils";
import { useInputFullName } from "../../fields/subForms/NamePrefixContext";
import { useFormHandlerContext } from "../../../../controllers/formController";
import GroupGridContainer from "../../../common/GroupGridContainer";
import { useFetchSubExistenceMap } from "../../../../hooks/query/useUserEnumQuery";

export interface UserEnumGroup {
  parent: string;
  child: string;
}

export interface UserEnumGroupWithGrandchild extends UserEnumGroup {
  grandchild: string;
}

interface UserEnumGroupViewProps {
  values: string[];
  emptyPlaceholder?: React.ReactNode;
}

export const UserEnumGroupView: React.FC<UserEnumGroupViewProps> = ({
  values,
  emptyPlaceholder = null,
}) => {
  // coerce to array
  const list = Array.isArray(values) ? values : [];

  if (list.length === 0) {
    // nothing to show
    return <>{emptyPlaceholder}</>;
  }

  return <span>{list.join(", ")}</span>;
};

interface UserEnumGroupFieldProps extends Grid2Props {
  name: string;
  parentActions?: React.ReactNode;
  childActions?: React.ReactNode;
  grandchildActions?: React.ReactNode;
  label: string;
  labelActions?: React.ReactNode;
  enableGrandchild?: boolean;
  parentLabel: string;
  childLabel: string;
  grandchildLabel?: string;
  parentUserEnum: string;
  disabled?: boolean;
  parentPlaceHolder?: string;
  childPlaceHolder?: string;
  grandchildPlaceHolder?: string;
  required?: boolean;
}

const UserEnumGroupField: React.FC<UserEnumGroupFieldProps> = ({
  name,
  label,
  labelActions,
  grandchildLabel,
  childLabel,
  parentLabel,
  parentUserEnum,
  enableGrandchild = false,
  parentPlaceHolder,
  childPlaceHolder,
  grandchildPlaceHolder,
  childActions,
  parentActions,
  grandchildActions,
  disabled = false,
  required = false,
  ...grid
}) => {
  const { watch, setValue } = useFormContext();
  const { isProcessing } = useFormHandlerContext();
  const fullName = useInputFullName(name);
  const parentValue: string = watch(`${fullName}.parent`);
  const childValue: string = watch(`${fullName}.child`);
  const { data: parentExistenceMap } = useFetchSubExistenceMap(parentUserEnum);
  const parentHasChildren = parentExistenceMap
    ? parentExistenceMap[parentValue] || false
    : false;

  useEffect(() => {
    setValue(`${fullName}.child`, "");
    if (enableGrandchild) {
      setValue(`${fullName}.grandchild`, "");
    }
  }, [parentValue, fullName, setValue, enableGrandchild]);

  useEffect(() => {
    if (enableGrandchild) {
      setValue(`${fullName}.grandchild`, "");
    }
  }, [childValue, fullName, setValue, enableGrandchild]);

  const showChild: boolean = Boolean(parentValue && parentHasChildren);

  const childEnum = showChild
    ? getEnumName(parentUserEnum, parentValue!)
    : undefined;

  const { data: childExistenceMap } = useFetchSubExistenceMap(childEnum || "");
  const childHasChildren = childExistenceMap
    ? childExistenceMap[childValue] || false
    : false;

  const showGrandchild: boolean = Boolean(
    showChild &&
      enableGrandchild &&
      grandchildLabel &&
      childValue &&
      childEnum &&
      childHasChildren
  );
  const grandchildEnum = showGrandchild
    ? getEnumName(childEnum!, childValue!)
    : undefined;

  const gridSize = { xs: 12, md: showGrandchild ? 4 : showChild ? 6 : 12 };

  return (
    <Grid {...singleGridItem} {...grid}>
      <FormLabel
        required={required}
        label={label}
        name={name}
        actions={labelActions}
      />
      <SubForm name={name}>
        <GroupGridContainer>
          <FormSelectField
            label={parentLabel}
            name="parent"
            useEnum={parentUserEnum}
            placeholder={parentPlaceHolder}
            required={required}
            size={gridSize}
            actions={parentActions}
            disabled={disabled || isProcessing}
          />

          <FormSelectField
            hidden={!showChild}
            label={childLabel}
            name="child"
            useEnum={childEnum}
            selfFetching
            placeholder={childPlaceHolder}
            required={required}
            size={gridSize}
            actions={childActions}
            disabled={disabled || isProcessing}
          />

          <FormSelectField
            hidden={!showGrandchild}
            label={grandchildLabel!}
            name="grandchild"
            useEnum={grandchildEnum}
            selfFetching
            placeholder={grandchildPlaceHolder}
            required={required}
            size={gridSize}
            actions={grandchildActions}
            disabled={disabled || isProcessing}
          />
        </GroupGridContainer>
      </SubForm>
    </Grid>
  );
};

export default UserEnumGroupField;
